import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Edit, Trash2, Eye } from 'lucide-react';
import { Collection, deleteCollection } from '@/utils/collectionsGraphQL';
import { EditCollectionModal } from './EditCollectionModal';
import { DeleteCollectionDialog } from './DeleteCollectionDialog';
import { useToast } from '@/hooks/use-toast';

interface CollectionTableProps {
  collections: Collection[];
  onDeleteSuccess?: () => void;
}

export const CollectionTable: React.FC<CollectionTableProps> = ({ 
  collections, 
  onDeleteSuccess 
}) => {
  const [editingCollection, setEditingCollection] = useState<Collection | null>(null);
  const [deletingCollection, setDeletingCollection] = useState<Collection | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEditSuccess = () => {
    setEditingCollection(null);
    if (onDeleteSuccess) onDeleteSuccess();
  };

  const handleDeleteClick = (collection: Collection) => {
    setDeletingCollection(collection);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingCollection) return;
    
    try {
      await deleteCollection(deletingCollection.id);
      setDeletingCollection(null);
      if (onDeleteSuccess) onDeleteSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete collection",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Assets</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="w-[70px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {collections.map((collection) => (
              <TableRow key={collection.id}>
                <TableCell>
                  <div className="font-medium">{collection.name}</div>
                </TableCell>
                <TableCell>
                  <div className="max-w-xs truncate text-sm text-gray-600">
                    {collection.description || 'No description'}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">
                    {collection.assets?.length || 0}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-gray-600">
                    {formatDate(collection.created_at)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-gray-600">
                    {formatDate(collection.updated_at)}
                  </div>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/collections/${collection.id}`)}>
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setEditingCollection(collection)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={() => handleDeleteClick(collection)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit Modal */}
      {editingCollection && (
        <EditCollectionModal
          open={!!editingCollection}
          onOpenChange={(open) => !open && setEditingCollection(null)}
          collection={editingCollection}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Delete Dialog */}
      {deletingCollection && (
        <DeleteCollectionDialog
          open={!!deletingCollection}
          onOpenChange={(open) => !open && setDeletingCollection(null)}
          collection={deletingCollection}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
};